import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { teacherStudentJourney, teacherStudentStops, teacherUnlock, teacherStudentSummary } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Unlock, AlertCircle, CheckCircle2, Lock, Printer, Download, Loader2, FileText, ClipboardList } from "lucide-react";
import ColdPoolTile from "@/components/ColdPoolTile";
import { formatTeacherText } from "@/lib/markdown";
import TestGeneratorModal from "@/components/TestGeneratorModal";
import TeacherTestResults from "@/components/TeacherTestResults";
import FLNCursorPanel from "@/components/FLNCursorPanel";

export default function TeacherStudent() {
  const { learnerId } = useParams();
  const { teacherId } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [journey, setJourney] = useState(null);
  const [stops, setStops] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const printRef = useRef(null);
  const [showTestGenerator, setShowTestGenerator] = useState(false);

  const refresh = async () => {
    const [j, s] = await Promise.all([
      teacherStudentJourney(learnerId),
      teacherStudentStops(learnerId),
    ]);
    setJourney(j);
    setStops(s);
  };

  // Fetch Claude-generated summary
  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await teacherStudentSummary(learnerId);
      // Only set summary if we got actual content (not empty strings from API failure)
      if (data.where_they_stand || data.suggested_next_steps) {
        setSummary(data);
      } else {
        setSummary(null);
      }
    } catch (err) {
      console.error("Summary generation failed:", err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { 
    refresh();
    fetchSummary();
  }, [learnerId]);

  const unlock = async (subject, sub_topic) => {
    await teacherUnlock(teacherId, learnerId, subject, sub_topic);
    refresh();
  };

  const handleBack = () => {
    // Check if we came from a cockpit view
    if (location.state?.from && location.state.from.includes('/teacher/cockpit/')) {
      navigate(location.state.from);
    } else {
      // Fallback: use browser back button
      navigate(-1);
    }
  };

  const handlePrint = () => {
    // Open print view in new tab
    window.open(`/teacher/student/${learnerId}/print`, '_blank');
  };

  const handleDownloadPDF = () => {
    // Open print view in new tab (user can save as PDF from print dialog)
    window.open(`/teacher/student/${learnerId}/print`, '_blank');
  };

  if (!journey) return <div className="text-center py-20 text-slate-400" data-testid="teacher-student-loading">Loading…</div>;

  const nodes = journey.sub_topic_nodes || [];
  const learnerName = journey.learner_name || "Student";
  const learnerGrade = journey.learner_grade || 5;
  
  // Determine subject for test generator pre-fill
  // Priority 1: Use cockpit subject if navigated from cockpit
  // Priority 2: Fallback to student's primary subject
  let preSelectedSubjectForTest = "—";
  
  if (location.state?.from && location.state.from.includes('/teacher/cockpit/')) {
    // Extract subject from cockpit URL: /teacher/cockpit/{grade}/{subject}
    const pathParts = location.state.from.split('/');
    if (pathParts.length >= 5) {
      const encodedSubject = pathParts[4];
      preSelectedSubjectForTest = decodeURIComponent(encodedSubject);
    }
  } else {
    // Fallback: use student's primary subject from journey
    preSelectedSubjectForTest = nodes.length > 0 ? nodes[0].subject : "—";
  }
  
  const primarySubject = nodes.length > 0 ? nodes[0].subject : "—";
  
  // Find weakest sub-topics from "Needs attention" - low accuracy or stops
  const needsAttentionNodes = nodes.filter(n => {
    const hasStops = (n.stops || []).length > 0;
    const accuracy = n.attempts > 0 ? (n.correct / n.attempts) : 0;
    return hasStops || (n.attempts >= 2 && accuracy < 0.5);
  });
  const weakestSubTopics = needsAttentionNodes.map(n => n.sub_topic);

  return (
    <div className="teacher-scope min-h-screen">
      {/* Print-specific CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-page { padding: 0 !important; }
          @page { size: A4 portrait; margin: 14mm 12mm; }
          .teacher-scope { min-height: 0; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" ref={printRef} data-testid="teacher-student-view">
        <div className="no-print flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <button onClick={handleBack} className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline tap-44" data-testid="back-teacher-dash">
            <ArrowLeft className="w-4 h-4" /> Back to class cockpit
          </button>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setShowTestGenerator(true)}
              size="sm"
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="generate-test-btn"
            >
              <FileText className="w-4 h-4" /> Generate Test
            </Button>
            <Button onClick={handlePrint} size="sm" variant="outline" className="flex items-center gap-2" data-testid="print-btn">
              <Printer className="w-4 h-4" /> Print
            </Button>
            <Button onClick={handleDownloadPDF} size="sm" variant="outline" className="flex items-center gap-2" data-testid="download-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>

        <h1 className="font-teacher text-3xl md:text-4xl font-bold text-slate-900 mb-1">{learnerName}'s Journey</h1>
        <p className="text-slate-600 mb-8">Diagnostic-grade view: every attempt, every stop, every unlock.</p>

        {/* Claude-generated summary sections */}
        {summaryLoading ? (
          <Card className="rounded-xl p-6 bg-sky-50 border-sky-200 mb-6">
            <div className="flex items-center gap-3 text-sky-700">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Generating teacher insights...</span>
            </div>
          </Card>
        ) : summary ? (
          <div className="space-y-4 mb-8">
            {summary.where_they_stand && (
              <Card className="rounded-xl p-6 bg-emerald-50 border-emerald-200" data-testid="summary-where-stands">
                <h3 className="font-teacher text-lg font-semibold text-emerald-900 mb-2">
                  Where {learnerName.split(' ')[0]} stands right now
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {formatTeacherText(summary.where_they_stand)}
                </p>
              </Card>
            )}
            {summary.suggested_next_steps && (
              <Card className="rounded-xl p-6 bg-violet-50 border-violet-200" data-testid="summary-next-steps">
                <h3 className="font-teacher text-lg font-semibold text-violet-900 mb-3">
                  Suggested next steps
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {formatTeacherText(summary.suggested_next_steps)}
                </p>
              </Card>
            )}
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2 className="font-teacher text-lg font-semibold text-slate-900 mb-3">Sub-topic nodes</h2>
            <div className="space-y-3" data-testid="teacher-nodes-list">
              {nodes.map((n, i) => {
                const isStopped = (n.stops || []).length > 0;
                const isHardLocked = n.status === "hard_locked";
                return (
                  <Card key={i} className={`rounded-xl p-4 bg-white border ${isHardLocked ? "border-slate-300 bg-slate-50/60" : "border-slate-200"}`} data-testid={`teacher-node-${n.sub_topic}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Link to={`/teacher/student/${learnerId}/subtopic/${encodeURIComponent(n.subject)}/${encodeURIComponent(n.sub_topic)}`} className="font-teacher font-semibold text-slate-900 hover:text-sky-700" data-testid={`drill-link-${n.sub_topic}`}>
                          {n.subject} • {n.sub_topic}
                        </Link>
                        {isHardLocked ? (
                          <div className="text-xs text-slate-600 mt-1" data-testid={`teacher-locked-msg-${n.sub_topic}`}>
                            Locked — <em className="not-italic font-semibold">{n.prerequisite_sub_topic}</em> not yet established. Tap to unlock manually.
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`teacher-pool-summary-${n.sub_topic}`}>
                            <span>{n.attempts} attempts • {n.correct} correct</span>
                            <span>status: <strong>{n.status}</strong></span>
                            {n.pool_a && (
                              <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold" data-testid={`teacher-pool-a-${n.sub_topic}`}>
                                Pool A: {n.pool_a.correct}/{n.pool_a.total}
                              </span>
                            )}
                            {n.pool_b && (
                              <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-semibold" data-testid={`teacher-pool-b-${n.sub_topic}`}>
                                Pool B: {n.pool_b.correct}/{n.pool_b.total}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {isStopped && (
                        <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1" data-testid={`teacher-node-stop-${n.sub_topic}`}>
                          <AlertCircle className="w-3.5 h-3.5" /> {n.stops.length} stop
                        </span>
                      )}
                      {!isStopped && n.status === "pool_a_complete" && (
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> done
                        </span>
                      )}
                      {n.unlocked_by_teacher && (
                        <span className="bg-sky-100 text-sky-800 text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1">
                          <Unlock className="w-3.5 h-3.5" /> unlocked
                        </span>
                      )}
                      {isHardLocked && !n.unlocked_by_teacher && (
                        <Button size="sm" onClick={() => unlock(n.subject, n.sub_topic)} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs h-8" data-testid={`teacher-unlock-locked-${n.sub_topic}`}>
                          <Lock className="w-3.5 h-3.5 mr-1" /> Unlock
                        </Button>
                      )}
                      {isStopped && !n.unlocked_by_teacher && !isHardLocked && (
                        <Button size="sm" onClick={() => unlock(n.subject, n.sub_topic)} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid={`teacher-unlock-${n.sub_topic}`}>
                          <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
              {nodes.length === 0 && <div className="text-center text-slate-400 py-6" data-testid="teacher-nodes-empty">No journey yet.</div>}
            </div>
          </div>

          <div>
            <h2 className="font-teacher text-lg font-semibold text-slate-900 mb-3">Stop history</h2>
            <div className="space-y-3" data-testid="teacher-stops-list">
              {stops.map(s => (
                <Card key={s.stop_id} className="rounded-xl p-4 bg-white border border-amber-200" data-testid={`teacher-stop-${s.stop_id}`}>
                  <div className="text-sm font-teacher font-semibold text-slate-900">{s.subject} • {s.sub_topic}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Layer: <strong>{s.trigger_layer}</strong> • Reroute: <strong>{s.reroute_type}</strong> → {s.rerouted_to}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{new Date(s.created_at).toLocaleString()}</div>
                  {s.trigger_questions?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-sky-700 font-semibold">Trigger questions ({s.trigger_questions.length})</summary>
                      <ul className="mt-2 space-y-2">
                        {s.trigger_questions.map((q, i) => (
                          <li key={i} className="bg-slate-50 rounded-md p-2 text-slate-700">
                            <div className="font-semibold">{q.question_text}</div>
                            <div className="text-slate-500 mt-1">Error: {q.error_type} • Diff: {q.difficulty}</div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </Card>
              ))}
              {stops.length === 0 && <div className="text-center text-slate-400 py-6" data-testid="teacher-stops-empty">No stops yet.</div>}
            </div>

            <div className="mt-6">
              <ColdPoolTile teacherId={teacherId} learnerId={learnerId} />
            </div>

            {/* Test Results for this student */}
            <div className="mt-8">
              <h2 className="font-teacher text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-slate-600" /> Test Results
              </h2>
              <TeacherTestResults learnerId={learnerId} />
            </div>
          </div>
        </div>

        {/* FLN Cursor Panel */}
        <FLNCursorPanel learnerId={learnerId} />
      </div>
      
      {/* Test Generator Modal */}
      <TestGeneratorModal
        open={showTestGenerator}
        onClose={() => setShowTestGenerator(false)}
        teacherId={teacherId}
        grade={learnerGrade}
        preSelectedSubject={preSelectedSubjectForTest}
        preSelectedSubTopics={weakestSubTopics}
        learnerName={learnerName}
        learnerId={learnerId}
      />
    </div>
  );
}
